import { Body, Button } from '@assembly-js/design-system';
import { t } from 'i18next';
import React, { useContext, useEffect, useState } from 'react';

import { TextField } from 'src/components/TextField';
import { PortalConfigContext } from 'src/context';
import { ModalWrapper } from 'src/legacy/components/Modals';
import { readIsProxySession } from 'src/utils/proxySession';

/** Shown wherever a charge action is disabled because the session is proxied. */
export const ProxyPurchaseNotice = () => {
  if (!readIsProxySession()) {
    return null;
  }
  return (
    <Body size="sm" className="text-gray-small">
      {t(($) => $.page_studio_plans.proxy_purchase.unavailable)}
    </Body>
  );
};

interface ProxyWorkspaceNameDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirmed: () => void;
}

/**
 * Cancel and pause stay available in a proxy session, but only after the
 * staff member types the workspace name shown in the dialog.
 */
export const ProxyWorkspaceNameDialog: React.FC<
  ProxyWorkspaceNameDialogProps
> = ({ open, onClose, onConfirmed }) => {
  const { name: workspaceName } = useContext(PortalConfigContext);
  const [typedName, setTypedName] = useState('');

  useEffect(() => {
    if (open) {
      setTypedName('');
    }
  }, [open]);

  const expectedName = workspaceName.trim();
  const nameMatches = expectedName !== '' && typedName.trim() === expectedName;

  return (
    <ModalWrapper
      open={open}
      onClose={onClose}
      hasBackdrop
      hideDividers
      hideActions
      denseContent
      closeButton
      width={546}
      title={t(($) => $.page_studio_plans.proxy_purchase.confirm_title)}
      description={t(
        ($) => $.page_studio_plans.proxy_purchase.confirm_description,
      )}
    >
      <div className="flex flex-col gap-4 px-5 pb-5">
        <div className="flex flex-col gap-2">
          <Body size="sm" className="font-medium text-black-heading">
            {t(($) => $.page_studio_plans.proxy_purchase.workspace_name_label)}
          </Body>
          <div className="select-all rounded border border-solid border-gray-150 bg-gray-100 px-3 py-2 text-black-heading">
            {workspaceName}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Body size="sm" className="font-medium text-black-heading">
            {t(($) => $.page_studio_plans.proxy_purchase.type_name_label)}
          </Body>
          <TextField
            id="proxyWorkspaceName"
            sizeVariant="standard"
            name="proxyWorkspaceName"
            type="text"
            variant="outlined"
            value={typedName}
            onChange={(event) => setTypedName(event.target.value)}
            fullWidth
            autoFocus
          />
        </div>
        <div className="flex gap-3">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="flex-1"
            label={t(($) => $.page_studio_plans.proxy_purchase.go_back)}
            onClick={onClose}
          />
          <Button
            type="button"
            variant="primary"
            size="sm"
            className="flex-1"
            label={t(($) => $.page_studio_plans.proxy_purchase.continue)}
            disabled={!nameMatches}
            onClick={onConfirmed}
          />
        </div>
      </div>
    </ModalWrapper>
  );
};

/**
 * Runs `action` immediately for a normal session. A proxy session waits until
 * the workspace name is typed.
 */
export const useProxyWorkspaceConfirmation = () => {
  const [open, setOpen] = useState(false);
  const pending = React.useRef<(() => void) | null>(null);

  const requestConfirmation = (action: () => void) => {
    if (!readIsProxySession()) {
      action();
      return;
    }
    pending.current = action;
    setOpen(true);
  };

  const close = () => {
    pending.current = null;
    setOpen(false);
  };

  const dialog = (
    <ProxyWorkspaceNameDialog
      open={open}
      onClose={close}
      onConfirmed={() => {
        const action = pending.current;
        pending.current = null;
        setOpen(false);
        action?.();
      }}
    />
  );

  return { requestConfirmation, dialog };
};
